import React, { useState, useEffect } from 'react';
import styles from './PaymentRequests.module.css';
import BankAccountForm from '../BankAccounts/BankAccountForm';
import { api } from '../../services/api';

const EMPTY = {
  bankAccountId: '',
  amount: '',
  description: '',
  note: '',
};

export default function PaymentRequestForm({ request, bankAccounts, onSave, onCancel, user, onAccountCreated, hideCancelButton, submitting }) {
  const [form, setForm] = useState(EMPTY);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [files, setFiles] = useState([]); // new files to upload
  const [existingAttachments, setExistingAttachments] = useState([]); // already uploaded

  useEffect(() => {
    if (request) {
      setForm({
        bankAccountId: request.bankAccountId,
        amount: request.amount,
        description: request.description,
        note: request.note || '',
      });
      // Load existing attachments
      api.getAttachments(user.credential, request.id).then(setExistingAttachments).catch(console.error);
    } else {
      setForm({ ...EMPTY, bankAccountId: bankAccounts[0]?.id || '' });
      setExistingAttachments([]);
    }
  }, [request, bankAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.bankAccountId) {
      alert('נא לבחור חשבון בנק');
      return;
    }
    const selected = bankAccounts.find((a) => a.id === parseInt(form.bankAccountId));
    const payload = {
      ...form,
      bankAccountId: parseInt(form.bankAccountId),
      amount: parseFloat(form.amount),
      bankName: selected?.bankName || '',
      branchNumber: selected?.branchNumber || '',
      accountNumber: selected?.accountNumber || '',
      accountOwnerName: selected?.accountOwnerName || '',
    };

    // Save request, then upload files
    onSave(payload, async (createdRequestId) => {
      if (files.length === 0 || !createdRequestId) return;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f.uploaded) continue;
        setFiles(prev => prev.map((x, j) => j === i ? { ...x, uploading: true } : x));
        try {
          const { url, s3Key } = await api.getUploadUrl(user.credential, createdRequestId, f.file.name, f.file.type || 'application/octet-stream');
          await fetch(url, { method: 'PUT', body: f.file, headers: { 'Content-Type': f.file.type || 'application/octet-stream' } });
          await api.confirmUpload(user.credential, createdRequestId, s3Key, f.file.name, f.file.type);
          setFiles(prev => prev.map((x, j) => j === i ? { ...x, uploading: false, uploaded: true } : x));
        } catch (err) {
          setFiles(prev => prev.map((x, j) => j === i ? { ...x, uploading: false, error: err.message } : x));
        }
      }
      setFiles([]);
    });
  };

  const handleNewAccountSave = async (accountData) => {
    const created = await api.createBankAccount(user.credential, accountData);
    setShowNewAccount(false);
    onAccountCreated(created);
    setForm({ ...form, bankAccountId: created.id });
  };

  return (
    <form onSubmit={submit} className={styles.form}>
      <h3>{request ? 'עריכת בקשה' : 'בקשת תשלום חדשה'}</h3>

      <label>חשבון בנק *
        <select name="bankAccountId" value={form.bankAccountId} onChange={handle} required>
          <option value="">-- בחר חשבון --</option>
          {bankAccounts.map((acc) => (
            <option key={acc.id} value={acc.id}>
              {acc.accountOwnerName} ({acc.bankName} {acc.branchNumber}/{acc.accountNumber})
            </option>
          ))}
        </select>
      </label>

      {!showNewAccount && (
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => setShowNewAccount(true)}
          style={{ marginBottom: 16, fontSize: 13 }}
        >
          + הוסף חשבון בנק חדש
        </button>
      )}

      {showNewAccount && (
        <div style={{ marginBottom: 16 }}>
          <BankAccountForm
            account={null}
            onSave={handleNewAccountSave}
            onCancel={() => setShowNewAccount(false)}
          />
        </div>
      )}

      <label>סכום (₪) *
        <input
          name="amount" type="number" step="0.01" min="0.01"
          value={form.amount} onChange={handle} required
        />
      </label>

      <label>תיאור הפעולה * <span style={{ fontSize: 12, color: '#999' }}>(כפי שייראה בתנועה בבנק)</span>
        <input name="description" value={form.description} onChange={handle} required maxLength={500} />
      </label>

      <label>הערה פרטית
        <textarea name="note" value={form.note} onChange={handle} maxLength={2000}
          placeholder="מידע פרטי — לא יופיע בבנק" />
      </label>

      <div>
        {/* Existing attachments (edit mode) */}
        {existingAttachments.length > 0 && (
          <div className={styles.fileList}>
            {existingAttachments.map((att) => (
              <div key={att.id} className={styles.fileItem}>
                <span>📎 {att.fileName}</span>
                <button type="button" className={styles.fileRemove} onClick={async () => {
                  if (!window.confirm('למחוק צרופה זו?')) return;
                  try {
                    await api.deleteAttachment(user.credential, att.id);
                    setExistingAttachments(prev => prev.filter(a => a.id !== att.id));
                  } catch (e) { alert('שגיאה במחיקה'); }
                }}>🗑️</button>
              </div>
            ))}
          </div>
        )}

        {/* Upload new files */}
        <label className={styles.attachBtn}>
          📎 צרף קבצים
          <input
            type="file"
            multiple
            accept="image/*,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const newFiles = Array.from(e.target.files).map(f => ({ file: f, uploading: false, uploaded: false, error: null }));
              setFiles(prev => [...prev, ...newFiles]);
              e.target.value = '';
            }}
          />
        </label>
        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map((f, i) => (
              <div key={i} className={styles.fileItem}>
                <span>{f.file.name}</span>
                {f.uploaded && <span className={styles.fileOk}>✓</span>}
                {f.uploading && <span className={styles.fileUploading}>...</span>}
                {f.error && <span className={styles.fileError}>✕</span>}
                {!f.uploaded && !f.uploading && (
                  <button type="button" className={styles.fileRemove} onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.formActions}>
        <button type="submit" className={styles.btnPrimary} disabled={submitting}>
          {submitting ? '⏳ שולח...' : 'שלח בקשה'}
        </button>
        {!hideCancelButton && <button type="button" className={styles.btnSecondary} onClick={onCancel}>ביטול</button>}
      </div>
    </form>
  );
}
